import { useState, type FormEvent } from 'react'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { Plus, Pencil } from 'lucide-react'
import { useProject } from './ProjectLayout'
import { Button, Card, Chip, ConfirmDialog, Drawer, cn } from '../../design'
import { useAuth } from '../../auth/AuthContext'
import {
  listEpics,
  createEpic,
  updateEpic,
  deleteEpic,
  type Epic,
  type EpicStatus,
} from '../../api/epics'

const EPIC_STATUSES: EpicStatus[] = ['planned', 'in_progress', 'done', 'cancelled']

const STATUS_TONE: Record<EpicStatus, 'neutral' | 'info' | 'success' | 'warning'> = {
  planned: 'neutral',
  in_progress: 'info',
  done: 'success',
  cancelled: 'warning',
}

// Matches the palette used for project colors elsewhere in the app.
const COLOR_SWATCHES = [
  '#7e57c2', '#6b9bd1', '#d97757', '#e8c468',
  '#8aa572', '#c2578f', '#57b6c2', '#9aa0a6',
]

const FORM_ID = 'new-epic-form'

const inputCls =
  'w-full rounded-md border border-border bg-bg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-accent/30 focus:border-accent'

export default function Epics() {
  const project = useProject()
  const [creating, setCreating] = useState(false)
  // A fresh key per open remounts the drawer, so its form and mutation start clean.
  const [drawerKey, setDrawerKey] = useState(0)
  const [editing, setEditing] = useState<Epic | null>(null)
  const openCreate = () => { setEditing(null); setDrawerKey(k => k + 1); setCreating(true) }
  const openEdit = (e: Epic) => { setEditing(e); setDrawerKey(k => k + 1); setCreating(true) }

  const epicsQ = useQuery({
    queryKey: ['epics', project.id],
    queryFn: () => listEpics(project.id),
    refetchOnWindowFocus: true,
  })

  const epics = epicsQ.data ?? []

  return (
    <div className="mx-auto max-w-5xl px-6 py-6 space-y-4">
      <div className="flex items-end justify-between">
        <div>
          <h2 className="text-base font-semibold tracking-tight">Epics</h2>
          <p className="text-xs text-fg-subtle">
            Group related stories in {project.key}. Epics become swimlanes on the board.
          </p>
        </div>
        <Button size="md" leftIcon={<Plus size={14} />} onClick={openCreate}>
          New epic
        </Button>
      </div>

      <Card padded={false}>
        {epicsQ.isLoading && (
          <div className="px-5 py-10 text-center text-sm text-fg-subtle">Loading epics…</div>
        )}
        {epicsQ.error && (
          <div className="px-5 py-10 text-center text-sm text-danger">Failed to load epics.</div>
        )}
        {!epicsQ.isLoading && !epicsQ.error && epics.length === 0 && (
          <div className="px-5 py-12 text-center">
            <p className="text-sm text-fg-subtle">No epics yet.</p>
            <p className="text-xs text-fg-subtle mt-1">
              Create one to group stories and give the board its swimlanes.
            </p>
            <div className="mt-4">
              <Button size="sm" leftIcon={<Plus size={12} />} onClick={openCreate}>
                New epic
              </Button>
            </div>
          </div>
        )}
        <ul className="divide-y divide-border">
          {epics.map(e => <EpicRow key={e.id} epic={e} onEdit={() => openEdit(e)} />)}
        </ul>
      </Card>

      <EpicDrawer
        key={drawerKey}
        projectId={project.id}
        epic={editing}
        open={creating}
        onOpenChange={setCreating}
      />
    </div>
  )
}

function EpicRow({ epic, onEdit }: { epic: Epic; onEdit: () => void }) {
  const total = epic.story_count ?? 0
  const done = epic.done_count ?? 0
  const pct = total > 0 ? Math.round((done / total) * 100) : 0

  return (
    <li>
      <button
        type="button"
        onClick={onEdit}
        title={`Edit ${epic.name}`}
        className="group w-full px-5 py-3 flex items-center gap-3 text-left hover:bg-bg-muted/40"
      >
      <span
        className="h-2.5 w-2.5 rounded-full shrink-0 ring-1 ring-bg-elevated"
        style={{ backgroundColor: epic.color ?? '#9aa0a6' }}
      />
      <div className="min-w-0 flex-1">
        <div className="text-sm font-medium truncate">{epic.name}</div>
        {epic.description && (
          <div className="text-xs text-fg-subtle truncate">{epic.description}</div>
        )}
      </div>

      {(epic.start_date || epic.due_date) && (
        <span className="text-[10px] font-mono text-fg-subtle whitespace-nowrap">
          {fmtDate(epic.start_date)} → {fmtDate(epic.due_date)}
        </span>
      )}

      <div className="w-28 shrink-0">
        <div className="flex items-center justify-between text-[10px] font-mono text-fg-subtle mb-0.5">
          <span>{done}/{total}</span>
          <span>{pct}%</span>
        </div>
        <div className="h-1.5 rounded-full bg-bg-muted overflow-hidden">
          <div
            className="h-full rounded-full bg-success transition-all"
            style={{ width: `${pct}%` }}
          />
        </div>
      </div>

      <Chip size="sm" tone={STATUS_TONE[epic.status]}>
        {epic.status.replace('_', ' ')}
      </Chip>
      <Pencil size={13} className="shrink-0 text-fg-disabled group-hover:text-fg-muted" aria-hidden />
      </button>
    </li>
  )
}

/**
 * Create-or-edit epic drawer (edit when `epic` is given, with Delete).
 *
 * Empty optional fields are omitted rather than sent blank: the backend's
 * validate() skips empty strings but still passes them through to the INSERT,
 * so an empty start_date/due_date would land in a DATE column.
 */
function EpicDrawer({
  projectId,
  epic,
  open,
  onOpenChange,
}: {
  projectId: number
  epic: Epic | null
  open: boolean
  onOpenChange: (open: boolean) => void
}) {
  const qc = useQueryClient()
  const { user } = useAuth()
  // Mirrors the server gate (requireProjectEditor): viewers cannot delete.
  const canDelete = !!epic && !!user && user.role !== 'viewer'
  const [confirmDelete, setConfirmDelete] = useState(false)

  const [name, setName] = useState(epic?.name ?? '')
  const [description, setDescription] = useState(epic?.description ?? '')
  const [color, setColor] = useState(epic?.color ?? COLOR_SWATCHES[0])
  const [status, setStatus] = useState<EpicStatus>(epic?.status ?? 'planned')
  const [startDate, setStartDate] = useState(epic?.start_date ?? '')
  const [dueDate, setDueDate] = useState(epic?.due_date ?? '')

  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['epics', projectId] })
    qc.invalidateQueries({ queryKey: ['lookups', 'epics', projectId] })
    qc.invalidateQueries({ queryKey: ['stories'] })
    qc.invalidateQueries({ queryKey: ['timeline'] })
  }

  const deleteMut = useMutation({
    mutationFn: () => deleteEpic(epic!.id),
    onSuccess: () => { invalidate(); onOpenChange(false) },
  })

  const createMut = useMutation({
    mutationFn: () => {
      if (epic) {
        // Edit: send every field; an empty description or date clears it.
        return updateEpic(epic.id, {
          name: name.trim(),
          status,
          color,
          description: description.trim(),
          start_date: startDate,
          due_date: dueDate,
        })
      }
      const payload = {
        project_id: projectId,
        name: name.trim(),
        status,
        color,
        ...(description.trim() ? { description: description.trim() } : {}),
        ...(startDate ? { start_date: startDate } : {}),
        ...(dueDate ? { due_date: dueDate } : {}),
      }
      return createEpic(payload)
    },
    onSuccess: () => { invalidate(); onOpenChange(false) },
  })


  const datesInvalid = !!startDate && !!dueDate && dueDate < startDate
  const canSubmit = name.trim().length > 0 && !datesInvalid && !createMut.isPending

  function onSubmit(e: FormEvent) {
    e.preventDefault()
    if (!canSubmit) return
    createMut.mutate()
  }

  return (
    <Drawer
      open={open}
      onOpenChange={onOpenChange}
      width="md"
      title={epic ? 'Edit epic' : 'New epic'}
      description={epic ? `${epic.story_count ?? 0} stories` : 'Only a name is required.'}
      footer={
        <div className="flex items-center gap-2">
          {canDelete && (
            <Button variant="outline" onClick={() => setConfirmDelete(true)} disabled={deleteMut.isPending}>
              {deleteMut.isPending ? 'Deleting…' : 'Delete'}
            </Button>
          )}
          {(createMut.isError || deleteMut.isError) && (
            <span className="text-xs text-danger">
              {((createMut.error ?? deleteMut.error) as Error)?.message ?? 'Something went wrong.'}
            </span>
          )}
          <div className="ml-auto flex gap-2">
            <Button variant="ghost" onClick={() => onOpenChange(false)}>Cancel</Button>
            <Button type="submit" form={FORM_ID} disabled={!canSubmit}>
              {createMut.isPending ? 'Saving…' : epic ? 'Save' : 'Create epic'}
            </Button>
          </div>
        </div>
      }
    >
      <form id={FORM_ID} onSubmit={onSubmit} className="space-y-4">
        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">
            Name<span className="text-danger"> *</span>
          </span>
          <input
            value={name}
            onChange={e => setName(e.target.value)}
            required
            autoFocus
            placeholder="e.g. Onboarding revamp"
            className={inputCls}
          />
        </label>

        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">Description</span>
          <textarea
            value={description}
            onChange={e => setDescription(e.target.value)}
            rows={3}
            placeholder="What does this epic cover?"
            className={cn(inputCls, 'resize-y')}
          />
        </label>

        <div>
          <span className="block text-xs font-medium text-fg-muted mb-1.5">Color</span>
          <div className="flex flex-wrap gap-2">
            {COLOR_SWATCHES.map(c => (
              <button
                key={c}
                type="button"
                onClick={() => setColor(c)}
                aria-label={`Color ${c}`}
                className={cn(
                  'h-7 w-7 rounded-full transition-transform',
                  color === c ? 'ring-2 ring-accent ring-offset-2 ring-offset-bg scale-110' : 'hover:scale-105',
                )}
                style={{ backgroundColor: c }}
              />
            ))}
          </div>
        </div>

        <label className="block">
          <span className="block text-xs font-medium text-fg-muted mb-1.5">Status</span>
          <select
            value={status}
            onChange={e => setStatus(e.target.value as EpicStatus)}
            className={inputCls}
          >
            {EPIC_STATUSES.map(s => (
              <option key={s} value={s}>{s.replace('_', ' ')}</option>
            ))}
          </select>
        </label>

        <div className="grid grid-cols-2 gap-3">
          <label className="block">
            <span className="block text-xs font-medium text-fg-muted mb-1.5">Start date</span>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              className={inputCls}
            />
          </label>
          <label className="block">
            <span className="block text-xs font-medium text-fg-muted mb-1.5">Due date</span>
            <input
              type="date"
              value={dueDate}
              onChange={e => setDueDate(e.target.value)}
              className={inputCls}
            />
          </label>
        </div>
        {datesInvalid && (
          <p className="text-xs text-danger">Due date must be on or after the start date.</p>
        )}
      </form>
      {epic && (
        <ConfirmDialog
          open={confirmDelete}
          onOpenChange={setConfirmDelete}
          title={`Delete ${epic.name}?`}
          description={`The epic and its comments are deleted. Its ${epic.story_count ?? 0} stories are kept — they just won't belong to an epic anymore.`}
          confirmLabel="Delete epic"
          onConfirm={() => deleteMut.mutate()}
        />
      )}
    </Drawer>
  )
}

function fmtDate(d: string | null): string {
  if (!d) return '—'
  return new Date(d + 'T00:00:00').toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
}
